// src/Components/Utils/TablePagination.jsx
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function TablePagination({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (totalPages <= 1) return null;

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const createPageNumbers = () => {
    const pages = [];

    // show all pages if small
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // many pages: 1 ... middle ... last
    if (currentPage <= 4) {
      // near the start
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      // near the end
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      // in the middle
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  const pages = createPageNumbers();

  return (
    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      {/* Left: info text */}
      <div className="text-xs text-gray-500">
        Showing{" "}
        <span className="font-semibold text-gray-700">
          {startItem}-{endItem}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-gray-700">{totalCount}</span>{" "}
        records
      </div>

      {/* Right: controls */}
      <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-1.5 py-1 shadow-sm">
        {/* Prev */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full text-xs
            ${
              currentPage === 1
                ? "cursor-not-allowed text-gray-300"
                : "text-gray-700 hover:bg-gray-100"
            }`}
        >
          <ChevronLeft size={14} />
        </button>

        {/* Page numbers */}
        {pages.map((item, idx) => {
          if (item === "...") {
            return (
              <span
                key={`dots-${idx}`}
                className="inline-flex h-8 min-w-8 items-center justify-center text-[11px] text-gray-400"
              >
                ...
              </span>
            );
          }

          const pageNumber = item;

          const isActive = pageNumber === currentPage;

          return (
            <button
              key={pageNumber}
              onClick={() => handlePageChange(pageNumber)}
              className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-[11px] font-medium transition
                ${
                  isActive
                    ? "bg-orange-500 text-white shadow-sm"
                    : "bg-transparent text-gray-700 hover:bg-gray-100"
                }`}
            >
              {pageNumber}
            </button>
          );
        })}

        {/* Next */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full text-xs
            ${
              currentPage === totalPages
                ? "cursor-not-allowed text-gray-300"
                : "text-gray-700 hover:bg-gray-100"
            }`}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
